import { DateTime } from "luxon";
import { PAGE_SIZE_OPTIONS } from "../../components/ServerTable";
import { capitalise } from "../../utils/string";
import { ViewSchema } from "./types";

const DATE_FORMAT = "yyyy-MM-dd";

const serialiseDate = (value: Date | null): string | null =>
  value ? DateTime.fromJSDate(value).toFormat(DATE_FORMAT) : null;

const deserialiseDate = (raw: string): Date | undefined => {
  const parsed = DateTime.fromFormat(raw, DATE_FORMAT);
  return parsed.isValid ? parsed.toJSDate() : undefined;
};

// consultant/applicationType/assignedUser/pageSize are applied by their
// respective fetchers only when they pass Number.isInteger - a value read
// back from the address bar is always a string, so this is the one place
// that coercion happens. Missing it means the filter silently does
// nothing, with no error anywhere (tech spec 4.5 / this story's own risk note).
const serialiseInteger = (value: number | null): string | null =>
  value === null ? null : String(value);

const deserialiseInteger = (raw: string): number | undefined => {
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : undefined;
};

/**
 * Sortable columns as defined in ApplicationsTable's own column list
 * (accessorKey values with a real header, i.e. excluding the blank
 * assigned-user avatar column and the "View" link column). Kept here
 * rather than imported because ApplicationsTable does not export its
 * column definitions; if that table's sortable columns change, this list
 * needs updating alongside it.
 */
export const APPLICATIONS_SORT_COLUMNS = [
  "name",
  "mandate_type__name",
  "status",
  "individual_count",
  "reference",
  "created_on",
];

interface OptionEntry {
  value: number | string;
  label: string;
}

const findLabel = (options: unknown[], value: unknown): string | undefined =>
  (options as OptionEntry[]).find((option) => option.value === value)?.label;

const isKnownOption = (options: unknown[], value: unknown): boolean =>
  (options as OptionEntry[]).some((option) => option.value === value);

export const applicationsView: ViewSchema = {
  dashboard: "applications",
  fields: {
    status: {
      param: "status",
      default: "all",
      // Either the literal string "all" or a numeric status id from
      // useStatusReport - "all" stays a string and is never coerced, but a
      // status id must become a real number: statusList's own `value` is
      // numeric (report[1].id), so a string here would fail the strict
      // equality check in both validate() below and the Select's own
      // "find the current option" lookup in Filters/index.tsx.
      serialise: (value: string | number) => (value === "all" ? null : String(value)),
      deserialise: (raw) => {
        if (raw === "all") return "all";
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : undefined;
      },
      optionsKey: "statuses",
      validate: (value, options) => value === "all" || isKnownOption(options, value),
      chipLabel: (value, options) =>
        `Status: ${capitalise(findLabel(options, value) ?? String(value))}`,
      inFilterPanel: true,
    },
    consultant: {
      param: "consultant",
      default: null,
      serialise: serialiseInteger,
      deserialise: deserialiseInteger,
      optionsKey: "consultants",
      validate: (value, options) => value === null || isKnownOption(options, value),
      chipLabel: (value, options) => `Consultant: ${findLabel(options, value) ?? value}`,
      inFilterPanel: true,
    },
    // Held as JS Date objects because useMandates.ts calls
    // DateTime.fromJSDate() on them directly. A range is two independent
    // parameters, each with its own default, so a one-ended range
    // round-trips without corrupting the other end.
    dateStartedFrom: {
      param: "from",
      default: null,
      serialise: serialiseDate,
      deserialise: deserialiseDate,
      chipLabel: (value: Date) =>
        `Started from: ${DateTime.fromJSDate(value).toFormat("d LLL yyyy")}`,
      inFilterPanel: true,
    },
    dateStartedTo: {
      param: "to",
      default: null,
      serialise: serialiseDate,
      deserialise: deserialiseDate,
      chipLabel: (value: Date) =>
        `Started to: ${DateTime.fromJSDate(value).toFormat("d LLL yyyy")}`,
      inFilterPanel: true,
    },
    applicationType: {
      param: "type",
      default: null,
      serialise: serialiseInteger,
      deserialise: deserialiseInteger,
      optionsKey: "applicationTypes",
      validate: (value, options) => value === null || isKnownOption(options, value),
      chipLabel: (value, options) => `Type: ${findLabel(options, value) ?? value}`,
      inFilterPanel: true,
    },
    includeArchived: {
      param: "archived",
      default: false,
      serialise: (value: boolean) => (value ? "true" : null),
      deserialise: (raw) => raw === "true",
      chipLabel: () => "Including archived",
      inFilterPanel: true,
    },
    // The topbar UserFilter, not a Filters-panel field: a different people
    // list (useUsers) from consultant (useConsultants) - each is validated
    // against its own list, never the other's. Gets a chip like any other
    // applied filter, but deliberately carries no inFilterPanel, so it
    // does not inflate the Filter button's own count.
    assignedUser: {
      param: "user",
      default: null,
      serialise: serialiseInteger,
      deserialise: deserialiseInteger,
      optionsKey: "users",
      validate: (value, options) => value === null || isKnownOption(options, value),
      chipLabel: (value, options) => `Assigned to: ${findLabel(options, value) ?? value}`,
    },
    // Session-scoped: restored on browser back and refresh, never written
    // to localStorage, never restored after the browser is closed and
    // reopened.
    search: {
      param: "q",
      default: "",
      serialise: (value: string) => (value === "" ? null : value),
      deserialise: (raw) => raw,
      session: true,
    },
    tab: {
      param: "tab",
      default: 0,
      serialise: (value: number) => (value === 0 ? null : String(value)),
      deserialise: (raw) => {
        const parsed = Number(raw);
        return [0, 1, 2].includes(parsed) ? parsed : undefined;
      },
      // options[0] carries the live profiles_enabled flag: tab 2 (Entity)
      // is only valid when Profiles is enabled for the tenant, matching
      // the page's own existing fallback-to-tab-0 effect (tech spec 4.4).
      optionsKey: "profilesEnabled",
      validate: (value, options) => value !== 2 || options[0] === true,
    },
    isCardView: {
      param: "view",
      default: true,
      serialise: (value: boolean) => (value ? null : "list"),
      deserialise: (raw) => raw !== "list",
    },
    sortBy: {
      param: "sort",
      default: "created_on",
      serialise: (value: string) => (value === "created_on" ? null : value),
      deserialise: (raw) => raw,
      optionsKey: "sortColumns",
      validate: (value, options) => (options as string[]).includes(value as string),
    },
    sortDesc: {
      param: "dir",
      default: true,
      serialise: (value: boolean) => (value ? null : "asc"),
      deserialise: (raw) => raw !== "asc",
    },
    pageSize: {
      param: "size",
      default: 10,
      serialise: (value: number) => (value === 10 ? null : String(value)),
      deserialise: (raw) => {
        const parsed = Number(raw);
        return Number.isInteger(parsed) ? parsed : undefined;
      },
      optionsKey: "pageSizes",
      validate: (value, options) => (options as number[]).includes(value as number),
    },
  },
};

/** Static option lists this schema needs that never depend on a network call. */
export const APPLICATIONS_STATIC_OPTION_LISTS = {
  sortColumns: APPLICATIONS_SORT_COLUMNS,
  pageSizes: PAGE_SIZE_OPTIONS,
};
